import { useMemo } from 'react';
import type { PublicChallenge, PublicStation } from '@cdf/api-client-react';

/** Conference day clock bounds (minutes from midnight). */
export const CONFERENCE_DAY_START_MINUTES = 8 * 60 + 30; // 08:30
export const CONFERENCE_DAY_END_MINUTES = 17 * 60; // 17:00
export const CONFERENCE_TIMELINE_STEP_MINUTES = 5;

export type ScheduleStatus = 'live' | 'upcoming' | 'ended' | 'unscheduled';

export interface ScheduleWindow {
  startTime?: string | null;
  endTime?: string | null;
}

export interface TimedStation extends PublicStation {
  status: ScheduleStatus;
  startMinutes: number | null;
  endMinutes: number | null;
}

export interface TimedChallenge extends PublicChallenge {
  status: ScheduleStatus;
  startMinutes: number | null;
  endMinutes: number | null;
}

export interface RoomScheduleStatus {
  room: string;
  status: ScheduleStatus;
  stations: TimedStation[];
  challenges: TimedChallenge[];
  /** Stations or challenges currently live in this room. */
  liveCount: number;
  upcomingCount: number;
  endedCount: number;
}

export interface TimeBasedHuntResult {
  selectedMinutes: number;
  selectedTime: string;
  stations: TimedStation[];
  challenges: TimedChallenge[];
  activeStations: TimedStation[];
  activeChallenges: TimedChallenge[];
  upcomingStations: TimedStation[];
  upcomingChallenges: TimedChallenge[];
  endedStations: TimedStation[];
  endedChallenges: TimedChallenge[];
  rooms: RoomScheduleStatus[];
  liveRooms: RoomScheduleStatus[];
  /** Unique session marker minutes derived from start/end times (within day bounds). */
  sessionMarkers: number[];
  counts: {
    live: number;
    upcoming: number;
    ended: number;
    unscheduled: number;
  };
}

const HH_MM_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** Parse `HH:mm` (24h) into minutes from midnight. Returns null if invalid/missing. */
export function parseScheduleMinutes(time: string | null | undefined): number | null {
  if (time == null || time === '') return null;
  const trimmed = time.trim();
  const match = HH_MM_RE.exec(trimmed);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours * 60 + minutes;
}

/** Format minutes-from-midnight as 24h `HH:mm`. */
export function formatScheduleTime24(totalMinutes: number): string {
  const clamped = clampMinutes(totalMinutes);
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Format minutes-from-midnight as 12h display, e.g. `8:30 AM`. */
export function formatScheduleTime12(totalMinutes: number): string {
  const clamped = clampMinutes(totalMinutes);
  const h24 = Math.floor(clamped / 60);
  const m = clamped % 60;
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function clampMinutes(
  totalMinutes: number,
  min = CONFERENCE_DAY_START_MINUTES,
  max = CONFERENCE_DAY_END_MINUTES,
): number {
  if (!Number.isFinite(totalMinutes)) return min;
  return Math.min(max, Math.max(min, Math.round(totalMinutes)));
}

export function snapMinutes(
  totalMinutes: number,
  step = CONFERENCE_TIMELINE_STEP_MINUTES,
  min = CONFERENCE_DAY_START_MINUTES,
  max = CONFERENCE_DAY_END_MINUTES,
): number {
  const clamped = clampMinutes(totalMinutes, min, max);
  const offset = clamped - min;
  const snapped = min + Math.round(offset / step) * step;
  return clampMinutes(snapped, min, max);
}

/**
 * Classify a schedule window relative to the selected event-clock minute.
 * - live: start ≤ selected < end (end exclusive when both present)
 * - missing end: live from start through end of day
 * - missing start: treated as unscheduled
 * - missing both: unscheduled
 */
export function getScheduleStatus(
  window: ScheduleWindow,
  selectedMinutes: number,
): ScheduleStatus {
  const start = parseScheduleMinutes(window.startTime);
  const end = parseScheduleMinutes(window.endTime);

  if (start == null && end == null) return 'unscheduled';
  if (start == null) {
    // End-only: active until end
    if (selectedMinutes < end!) return 'live';
    return 'ended';
  }

  const effectiveEnd = end ?? CONFERENCE_DAY_END_MINUTES;

  if (selectedMinutes < start) return 'upcoming';
  if (selectedMinutes >= effectiveEnd) return 'ended';
  return 'live';
}

function withStatus<T extends ScheduleWindow>(
  item: T,
  selectedMinutes: number,
): T & { status: ScheduleStatus; startMinutes: number | null; endMinutes: number | null } {
  return {
    ...item,
    status: getScheduleStatus(item, selectedMinutes),
    startMinutes: parseScheduleMinutes(item.startTime),
    endMinutes: parseScheduleMinutes(item.endTime),
  };
}

function roomKey(room: string | null | undefined): string {
  const value = room?.trim();
  return value && value.length > 0 ? value : 'Unassigned';
}

function worstRoomStatus(statuses: ScheduleStatus[]): ScheduleStatus {
  if (statuses.includes('live')) return 'live';
  if (statuses.includes('upcoming')) return 'upcoming';
  if (statuses.includes('ended')) return 'ended';
  return 'unscheduled';
}

export function computeTimeBasedHunt(
  stations: PublicStation[],
  challenges: PublicChallenge[],
  selectedTime: string | number,
): TimeBasedHuntResult {
  const selectedMinutes =
    typeof selectedTime === 'number'
      ? clampMinutes(selectedTime)
      : clampMinutes(parseScheduleMinutes(selectedTime) ?? CONFERENCE_DAY_START_MINUTES);

  const timedStations = stations.map((s) => withStatus(s, selectedMinutes));
  const timedChallenges = challenges.map((c) => withStatus(c, selectedMinutes));

  const activeStations = timedStations.filter((s) => s.status === 'live');
  const activeChallenges = timedChallenges.filter((c) => c.status === 'live');
  const upcomingStations = timedStations.filter((s) => s.status === 'upcoming');
  const upcomingChallenges = timedChallenges.filter((c) => c.status === 'upcoming');
  const endedStations = timedStations.filter((s) => s.status === 'ended');
  const endedChallenges = timedChallenges.filter((c) => c.status === 'ended');

  const roomMap = new Map<string, RoomScheduleStatus>();

  const ensureRoom = (room: string): RoomScheduleStatus => {
    let entry = roomMap.get(room);
    if (!entry) {
      entry = {
        room,
        status: 'unscheduled',
        stations: [],
        challenges: [],
        liveCount: 0,
        upcomingCount: 0,
        endedCount: 0,
      };
      roomMap.set(room, entry);
    }
    return entry;
  };

  for (const station of timedStations) {
    if (station.status === 'unscheduled' && !station.room) continue;
    const entry = ensureRoom(roomKey(station.room));
    entry.stations.push(station);
    if (station.status === 'live') entry.liveCount += 1;
    else if (station.status === 'upcoming') entry.upcomingCount += 1;
    else if (station.status === 'ended') entry.endedCount += 1;
  }

  for (const challenge of timedChallenges) {
    if (challenge.status === 'unscheduled' && !challenge.room) continue;
    const entry = ensureRoom(roomKey(challenge.room));
    entry.challenges.push(challenge);
    if (challenge.status === 'live') entry.liveCount += 1;
    else if (challenge.status === 'upcoming') entry.upcomingCount += 1;
    else if (challenge.status === 'ended') entry.endedCount += 1;
  }

  const rooms = Array.from(roomMap.values())
    .map((entry) => {
      const statuses = [
        ...entry.stations.map((s) => s.status),
        ...entry.challenges.map((c) => c.status),
      ];
      return { ...entry, status: worstRoomStatus(statuses) };
    })
    .sort((a, b) => {
      const order: Record<ScheduleStatus, number> = {
        live: 0,
        upcoming: 1,
        ended: 2,
        unscheduled: 3,
      };
      const byStatus = order[a.status] - order[b.status];
      if (byStatus !== 0) return byStatus;
      return a.room.localeCompare(b.room);
    });

  const markerSet = new Set<number>();
  for (const item of [...timedStations, ...timedChallenges]) {
    if (item.startMinutes != null) {
      const m = clampMinutes(item.startMinutes);
      if (m >= CONFERENCE_DAY_START_MINUTES && m <= CONFERENCE_DAY_END_MINUTES) {
        markerSet.add(m);
      }
    }
    if (item.endMinutes != null) {
      const m = clampMinutes(item.endMinutes);
      if (m >= CONFERENCE_DAY_START_MINUTES && m <= CONFERENCE_DAY_END_MINUTES) {
        markerSet.add(m);
      }
    }
  }

  const unscheduledCount =
    timedStations.filter((s) => s.status === 'unscheduled').length +
    timedChallenges.filter((c) => c.status === 'unscheduled').length;

  return {
    selectedMinutes,
    selectedTime: formatScheduleTime24(selectedMinutes),
    stations: timedStations,
    challenges: timedChallenges,
    activeStations,
    activeChallenges,
    upcomingStations,
    upcomingChallenges,
    endedStations,
    endedChallenges,
    rooms,
    liveRooms: rooms.filter((r) => r.status === 'live'),
    sessionMarkers: Array.from(markerSet).sort((a, b) => a - b),
    counts: {
      live: activeStations.length + activeChallenges.length,
      upcoming: upcomingStations.length + upcomingChallenges.length,
      ended: endedStations.length + endedChallenges.length,
      unscheduled: unscheduledCount,
    },
  };
}

export interface UseTimeBasedHuntOptions {
  stations: PublicStation[];
  challenges: PublicChallenge[];
  /** Event-clock time as `HH:mm` or minutes from midnight. */
  selectedTime: string | number;
}

/**
 * Derive which hunt stations/challenges are live, upcoming, or ended
 * at a selected conference timeline position.
 */
export function useTimeBasedHunt({
  stations,
  challenges,
  selectedTime,
}: UseTimeBasedHuntOptions): TimeBasedHuntResult {
  return useMemo(
    () => computeTimeBasedHunt(stations, challenges, selectedTime),
    [stations, challenges, selectedTime],
  );
}
